/**
 * Turning a re-close plan into wall geometry.
 *
 * "Walls follow corners": when the boundary gains a corner, the real walls that
 * meet there move their gap-side END to it. Two different rules, and the
 * difference is deliberate —
 *
 *   - a ROOM wall (a zero-thickness face) is re-seated on the new corner in
 *     both directions: it is a face of this room's boundary and the boundary
 *     just moved;
 *   - a STRUCTURE wall is only ever EXTENDED to reach the corner, never
 *     shortened back to it. A scanned wall that overshoots is carrying real
 *     measured length that belongs to whatever else is on the other side of
 *     it — trimming it is not this edit's to do. Same asymmetry as the
 *     pipeline's own gap closer (Step 2.35).
 *
 * Pure.
 */

const { edgeFrame } = require('./runs');

const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

/** Which of a wall's two alignment ends is nearer `p` — 'bottomLeft' | 'bottomRight'. */
function nearerEnd(wall, p) {
  const bl = wall?.alignment?.bottomLeft;
  const br = wall?.alignment?.bottomRight;
  if (!bl || !br) return null;
  return dist(bl, p) <= dist(br, p) ? 'bottomLeft' : 'bottomRight';
}

/**
 * A room wall with one end moved to `target`. Rebuilds every field derived from
 * the endpoints — alignment, centre, length, rotation and the face polygon —
 * so nothing keeps describing the old span.
 *
 * @param {object} wall the room wall
 * @param {{x,z}} nearPoint the end to move, named by proximity
 * @param {{x,z}} target where it goes
 */
function moveWallEnd(wall, nearPoint, target) {
  const which = nearerEnd(wall, nearPoint);
  if (!which) return wall;
  const other = which === 'bottomLeft' ? 'bottomRight' : 'bottomLeft';
  const y = wall.alignment[which].y ?? 0;
  const moved = { x: target.x, y, z: target.z };
  const fixed = wall.alignment[other];
  const f = edgeFrame(
    which === 'bottomLeft' ? moved : fixed,
    which === 'bottomLeft' ? fixed : moved
  );
  if (!f || f.L < 1e-6) return wall;

  const bl = which === 'bottomLeft' ? moved : fixed;
  const br = which === 'bottomLeft' ? fixed : moved;
  const height = wall.dimensions?.y ?? 0;
  const floorY = bl.y ?? 0;
  return {
    ...wall,
    center: {
      x: (bl.x + br.x) / 2,
      y: wall.center?.y ?? floorY + height / 2,
      z: (bl.z + br.z) / 2,
    },
    dimensions: { ...wall.dimensions, x: f.L },
    rotation: { ...wall.rotation, y: Math.atan2(-(br.z - bl.z), br.x - bl.x) },
    polygonCorners: [
      { x: bl.x, y: floorY, z: bl.z },
      { x: br.x, y: floorY, z: br.z },
      { x: br.x, y: floorY + height, z: br.z },
      { x: bl.x, y: floorY + height, z: bl.z },
    ],
    alignment: {
      ...wall.alignment,
      bottomLeft: bl,
      bottomRight: br,
      bottomMiddle: {
        x: (bl.x + br.x) / 2,
        y: floorY,
        z: (bl.z + br.z) / 2,
      },
    },
  };
}

/**
 * A structure wall grown so its nearer end reaches `target`, or null when it
 * already reaches past it. Centre and length only — a structure wall has no
 * alignment corners.
 *
 * @returns {{ wallId, center, dimensions, rotation } | null} an update in the
 *   shape `updateStructureWallsGeometry` takes.
 */
function extendStructureWallTo(sw, target) {
  const ry = sw?.rotation?.y ?? 0;
  const dir = { x: Math.cos(ry), z: -Math.sin(ry) };
  const c = sw?.center;
  const L = sw?.dimensions?.x;
  if (!c || !(L > 0)) return null;
  const half = L / 2;
  const ends = [
    { x: c.x - dir.x * half, z: c.z - dir.z * half },
    { x: c.x + dir.x * half, z: c.z + dir.z * half },
  ];
  const i = dist(ends[0], target) <= dist(ends[1], target) ? 0 : 1;
  // How far past its own end the target sits, measured along the wall's axis.
  const outward = i === 0 ? -1 : 1;
  const beyond =
    ((target.x - ends[i].x) * dir.x + (target.z - ends[i].z) * dir.z) * outward;
  if (beyond <= 0) return null; // already reaches past it — leave the overshoot
  const newEnd = {
    x: ends[i].x + dir.x * beyond * outward,
    z: ends[i].z + dir.z * beyond * outward,
  };
  const keep = ends[1 - i];
  return {
    wallId: sw.id,
    center: {
      x: (newEnd.x + keep.x) / 2,
      y: c.y,
      z: (newEnd.z + keep.z) / 2,
    },
    dimensions: { ...sw.dimensions, x: L + beyond },
    rotation: { ...sw.rotation },
    grewBy: beyond,
  };
}

module.exports = { nearerEnd, moveWallEnd, extendStructureWallTo };
